<template>
  <div>
    <h2
      class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2"
      style="color: var(--text-color)"
    >
      Snippets
    </h2>

    <SnippetsConfirmDialog />

    <TabView
      :activeIndex="activeTabIndex"
      @tab-change="(e) => setTab(tabs[e.index])"
    >
      <TabPanel header="Audio Snippets">
        <Card>
          <template #header>
            <div
              class="p-3 pb-0 flex flex-column sm:flex-row justify-content-between align-items-start sm:align-items-center gap-3"
            >
              <Button
                icon="pi pi-refresh"
                text
                rounded
                aria-label="Refresh"
                v-tooltip.bottom="'Refresh'"
                :loading="loading"
                @click="refreshSnippets"
              />
              <div class="flex flex-column sm:flex-row gap-3">
                <div class="flex align-items-center gap-1 w-full sm:w-20rem">
                  <Calendar
                    input-id="snippets-date-range"
                    v-model="dateRange"
                    :showIcon="true"
                    :number-of-months="1"
                    :selectionMode="'range'"
                    placeholder="Search by date range"
                    class="w-full"
                    @update:modelValue="setDateRange"
                  />
                  <i
                    v-if="dateRange"
                    class="pi pi-times cursor-pointer"
                    v-tooltip.bottom="'Clear date range'"
                    @click="clearDateRange"
                  ></i>
                </div>
                <InputText
                  class="w-full sm:w-24rem"
                  type="text"
                  v-model="searchQuery"
                  @input="onSearchInput"
                  placeholder="Search show or episode"
                />
              </div>
            </div>
          </template>
          <template #content>
            <div class="overflow-x-auto">
              <DataTable
                :value="snippets"
                class="p-datatable-sm"
                lazy
                :paginator="true"
                :rows="10"
                :total-records="totalSnippets"
                :loading="loading"
                @page="onPage"
              >
                <Column field="id" header="ID">
                  <template #body="slotProps">
                    <span class="text-500 text-sm">{{
                      slotProps.data.id
                    }}</span>
                  </template>
                </Column>

                <Column
                  field="show_title"
                  header="Show"
                  style="min-width: 127px"
                />

                <Column field="episode_title" header="Episode">
                  <template #body="slotProps">
                    <span
                      class="episode-title-cell"
                      v-tooltip.top="slotProps.data.episode_title"
                    >
                      {{ slotProps.data.episode_title }}
                    </span>
                  </template>
                </Column>

                <Column header="Audio">
                  <template #body="slotProps">
                    <audio
                      controls
                      preload="none"
                      class="snippet-audio-player"
                      :src="slotProps.data.snippet_audio_url"
                    />
                    <div
                      v-if="
                        slotProps.data.metadata?.clean_audio_url &&
                        slotProps.data.metadata.clean_audio_url !==
                          slotProps.data.snippet_audio_url
                      "
                      class="flex align-items-center gap-2 text-xs"
                    >
                      <span class="text-500">
                        playing: with fade
                        {{ slotProps.data.metadata.fade_out_sec }}s
                      </span>
                      <a
                        :href="slotProps.data.metadata.clean_audio_url"
                        target="_blank"
                        rel="noopener"
                        >compare original</a
                      >
                    </div>
                  </template>
                </Column>

                <Column field="duration_sec" header="Duration">
                  <template #body="slotProps">
                    {{ slotProps.data.duration_sec }}s
                  </template>
                </Column>

                <Column header="Transcript">
                  <template #body="slotProps">
                    <Button
                      v-if="slotProps.data.metadata?.transcript_text"
                      label="View"
                      size="small"
                      text
                      @click="openTranscript(slotProps.data)"
                    />
                    <span v-else class="text-500 text-sm">None</span>
                  </template>
                </Column>

                <Column header="Validation Note" style="min-width: 118px">
                  <template #body="slotProps">
                    <ValidationNoteCell
                      :text="slotProps.data.validation_note"
                    />
                  </template>
                </Column>

                <Column
                  field="created_at"
                  header="Created At"
                  style="min-width: 206px"
                >
                  <template #body="slotProps">
                    {{ formatDate(slotProps.data.created_at) }}
                  </template>
                </Column>

                <Column field="is_disabled">
                  <template #header>
                    <span class="flex align-items-center" style="gap: 5px">
                      Disabled
                      <i
                        class="pi pi-info-circle text-500"
                        v-tooltip.top="
                          'Disabled snippets are excluded from playlists for sellers with restricted validation'
                        "
                      />
                    </span>
                  </template>
                  <template #body="slotProps">
                    <div class="text-center">
                      {{ slotProps.data.is_disabled ? "Yes" : "No" }}
                    </div>
                  </template>
                </Column>

                <Column header="Actions">
                  <template #body="slotProps">
                    <div class="flex align-items-center gap-2">
                      <RegenerationStatusCell
                        :regenerating="slotProps.data.regenerating"
                        :just-regenerated="
                          justRegeneratedIds.has(slotProps.data.id)
                        "
                        :loading="regeneratingClickedId === slotProps.data.id"
                        @regenerate="confirmRegenerateSnippet(slotProps.data)"
                      />
                      <Button
                        :label="
                          slotProps.data.is_disabled ? 'Enable' : 'Disable'
                        "
                        :severity="
                          slotProps.data.is_disabled ? 'success' : 'danger'
                        "
                        size="small"
                        outlined
                        class="snippet-toggle-btn"
                        @click="
                          toggleSnippetEnabled(
                            slotProps.data,
                            !!slotProps.data.is_disabled
                          )
                        "
                      />
                    </div>
                  </template>
                </Column>
              </DataTable>
            </div>
          </template>
        </Card>
      </TabPanel>

      <TabPanel header="Video Snippets">
        <TabView
          :activeIndex="videoSubTabIndex"
          @tab-change="(e) => (videoSubTabIndex = e.index)"
        >
          <TabPanel header="Static">
            <AdminPodcastGeneratedVideos
              embedded
              mode="static"
              :initial-show-id="videoLibraryShowId"
            />
          </TabPanel>
          <TabPanel header="Dynamic">
            <AdminPodcastGeneratedVideos embedded mode="dynamic" />
          </TabPanel>
        </TabView>
      </TabPanel>

      <TabPanel header="Shows">
        <Card>
          <template #header>
            <div
              class="p-3 pb-0 flex flex-column sm:flex-row justify-content-between align-items-start sm:align-items-center gap-3"
            >
              <div class="flex align-items-center gap-2">
                <Button
                  icon="pi pi-refresh"
                  text
                  rounded
                  aria-label="Refresh"
                  v-tooltip.bottom="'Refresh'"
                  :loading="showsLoading"
                  @click="fetchShows"
                />
                <InputText
                  class="w-full sm:w-20rem"
                  type="text"
                  v-model="showsSearchQuery"
                  @input="onShowsSearchInput"
                  placeholder="Search shows"
                />
              </div>
              <Button
                label="Add Show"
                icon="pi pi-plus"
                size="small"
                @click="openAddShowDialog"
              />
            </div>
          </template>
          <template #content>
            <DataTable
              :value="shows"
              class="p-datatable-sm"
              :paginator="true"
              :rows="10"
              :loading="showsLoading"
            >
              <Column
                field="show_title"
                header="Show"
                :sortable="true"
                style="width: 28%"
              />

              <Column
                field="episode_count"
                header="Audio Episodes"
                :sortable="true"
              />

              <Column
                field="podcast_videos_count"
                header="Video Episodes"
                :sortable="true"
              />

              <Column
                field="snippet_count"
                header="Audio Snippets Generated"
                :sortable="true"
              >
                <template #body="slotProps">
                  <Badge
                    :severity="
                      slotProps.data.snippet_count > 0 ? 'success' : 'secondary'
                    "
                    :value="slotProps.data.snippet_count"
                  />
                </template>
              </Column>

              <Column
                field="podcast_generated_videos_count"
                header="Video Snippets Generated"
                :sortable="true"
              >
                <template #body="slotProps">
                  <Badge
                    :severity="
                      slotProps.data.podcast_generated_videos_count > 0
                        ? 'success'
                        : 'secondary'
                    "
                    :value="slotProps.data.podcast_generated_videos_count"
                  />
                </template>
              </Column>

              <Column
                field="active_seller_count"
                header="Active Sellers"
                :sortable="true"
                style="width: 8%"
              >
                <template #body="slotProps">
                  <Badge
                    :severity="
                      slotProps.data.active_seller_count > 0
                        ? 'info'
                        : 'secondary'
                    "
                    :value="slotProps.data.active_seller_count"
                  />
                </template>
              </Column>

              <Column
                header="Audio Snippets"
                :bodyStyle="{
                  'text-align': 'center',
                  'vertical-align': 'middle',
                }"
              >
                <template #body="slotProps">
                  <AutomationBadge
                    :enabled="
                      slotProps.data.snippets_generation_config?.enabled
                    "
                    :active-seller-count="slotProps.data.active_seller_count"
                  />
                </template>
              </Column>

              <Column
                header="Static Videos Snippets"
                :bodyStyle="{
                  'text-align': 'center',
                  'vertical-align': 'middle',
                }"
              >
                <template #body="slotProps">
                  <AutomationBadge
                    :enabled="
                      slotProps.data.video_snippets_generation_config?.static
                        ?.is_auto_generation_enabled
                    "
                    :active-seller-count="slotProps.data.active_seller_count"
                  />
                </template>
              </Column>

              <Column
                header="Dynamic Video Snippets"
                :bodyStyle="{
                  'text-align': 'center',
                  'vertical-align': 'middle',
                }"
              >
                <template #body="slotProps">
                  <AutomationBadge
                    :enabled="
                      slotProps.data.video_snippets_generation_config?.dynamic
                        ?.enabled
                    "
                    :active-seller-count="slotProps.data.active_seller_count"
                  />
                </template>
              </Column>

              <Column style="width: 14rem">
                <template #body="slotProps">
                  <div class="show-actions-row">
                    <Button
                      icon="pi pi-microphone"
                      v-tooltip.left="
                        slotProps.data.snippet_count > 0
                          ? 'View Audio Snippets'
                          : 'No snippets generated yet'
                      "
                      outlined
                      rounded
                      size="small"
                      class="round-icon-btn"
                      :disabled="slotProps.data.snippet_count === 0"
                      @click="viewSnippetsForShow(slotProps.data)"
                      aria-label="View Audio Snippets"
                    />
                    <Button
                      icon="pi pi-video"
                      v-tooltip.left="
                        slotProps.data.podcast_generated_videos_count > 0
                          ? 'View Video Snippets'
                          : 'No videos generated yet'
                      "
                      outlined
                      rounded
                      size="small"
                      class="round-icon-btn"
                      :disabled="!slotProps.data.podcast_generated_videos_count"
                      @click="openViewVideosDialog(slotProps.data)"
                      aria-label="View Video Snippets"
                    />
                    <Button
                      icon="pi pi-plus"
                      v-tooltip.left="
                        generatingShowIds.has(slotProps.data.show_id)
                          ? 'Currently generating — wait until it finishes'
                          : 'Generate Snippets'
                      "
                      outlined
                      rounded
                      severity="success"
                      size="small"
                      class="round-icon-btn"
                      :disabled="generatingShowIds.has(slotProps.data.show_id)"
                      @click="openEpisodeSelectDialog(slotProps.data)"
                      aria-label="Generate Snippets"
                    />
                    <Button
                      icon="pi pi-pencil"
                      v-tooltip.left="'Edit Snippet Config'"
                      outlined
                      rounded
                      size="small"
                      class="round-icon-btn"
                      @click="openEditShowDialog(slotProps.data)"
                      aria-label="Edit Snippet Config"
                    />
                    <Button
                      icon="pi pi-trash"
                      v-tooltip.left="removeShowTooltip(slotProps.data)"
                      outlined
                      rounded
                      severity="danger"
                      size="small"
                      class="round-icon-btn"
                      :disabled="!canRemoveShow(slotProps.data)"
                      @click="confirmRemoveShow(slotProps.data)"
                      aria-label="Remove Show"
                    />
                  </div>
                </template>
              </Column>
            </DataTable>
          </template>
        </Card>
      </TabPanel>
    </TabView>

    <TranscriptDialog
      v-model:visible="showTranscriptDialog"
      :text="selectedTranscript"
    />

    <Dialog
      v-model:visible="showAddShowDialog"
      :header="editingShow ? 'Edit Snippet Config' : 'Add Show'"
      modal
      :style="{ width: '42rem' }"
    >
      <div ref="addShowStepWrapper" class="add-show-step-wrapper">
        <div v-if="!showAddShowConfirmStep" class="flex flex-column gap-3">
          <p class="add-show-intro">
            Adding a show here lets you turn on automatic snippet generation and
            also generate snippets for it manually — a show must be on this list
            for either to work.
          </p>

          <div class="flex flex-column gap-1">
            <label v-if="!editingShow" for="addShowDropdown">Show</label>
            <Dropdown
              v-if="!editingShow"
              id="addShowDropdown"
              v-model="showToAdd"
              :options="availableShowsToAdd"
              optionLabel="show_title"
              filter
              placeholder="Select a show"
              class="w-full"
              :panelStyle="{
                width: 'min(26rem, 96vw)',
                maxHeight: '16rem',
                overflow: 'hidden',
              }"
              :loading="availableShowsLoading"
            />
            <template v-else>
              <label>Show</label>
              <div class="p-2 border-round surface-100 font-medium">
                {{ editingShow.show_title }}
              </div>
            </template>
          </div>

          <div class="add-show-columns">
            <div class="add-show-column">
              <div class="add-show-column-header">
                <span class="add-show-column-title"
                  >Audio Snippets Automation</span
                >
                <InputSwitch v-model="audioSnippetsEnabled" />
              </div>
              <p class="add-show-column-desc">
                Each time this show publishes a new episode, a snippet is
                generated automatically.
              </p>

              <div
                class="flex flex-column gap-3"
                :class="{ 'add-show-column-disabled': !audioSnippetsEnabled }"
              >
                <div class="flex flex-column gap-1">
                  <label for="snippetDuration">Snippet Length (seconds)</label>
                  <InputNumber
                    id="snippetDuration"
                    v-model="newShowDuration"
                    :disabled="!audioSnippetsEnabled"
                    class="w-full"
                    :class="{ 'p-invalid': durationError }"
                  />
                  <small :class="durationError ? 'p-error' : 'text-500'">
                    {{ durationError || "Between 60 and 180 seconds." }}
                  </small>
                </div>

                <div class="flex flex-column gap-1">
                  <label for="snippetsPerEpisode">Snippets per Episode</label>
                  <InputNumber
                    id="snippetsPerEpisode"
                    v-model="newShowSnippetsPerEpisode"
                    :min="1"
                    :max="5"
                    :disabled="!audioSnippetsEnabled"
                    class="w-full"
                    :class="{ 'p-invalid': snippetsPerEpisodeError }"
                  />
                  <small
                    :class="snippetsPerEpisodeError ? 'p-error' : 'text-500'"
                  >
                    {{
                      snippetsPerEpisodeError ||
                      "1 to 5. Required when automation is enabled."
                    }}
                  </small>
                </div>

                <div class="flex flex-column gap-1">
                  <label
                    >Generate Static Video Snippets Automatically for each new
                    audio snippet</label
                  >
                  <div class="flex align-items-center gap-2">
                    <InputSwitch
                      inputId="videoGenerationEnabled"
                      v-model="newShowVideoGenerationEnabled"
                      :disabled="!audioSnippetsEnabled"
                    />
                    <label for="videoGenerationEnabled" class="cursor-pointer">
                      Enabled
                    </label>
                  </div>
                </div>

                <div class="flex flex-column gap-1">
                  <Button
                    :label="showAdvancedAudio ? 'Hide advanced' : 'Advanced'"
                    :icon="
                      showAdvancedAudio
                        ? 'pi pi-chevron-up'
                        : 'pi pi-chevron-down'
                    "
                    text
                    class="align-self-start p-0"
                    :disabled="!audioSnippetsEnabled"
                    @click="showAdvancedAudio = !showAdvancedAudio"
                  />

                  <template v-if="showAdvancedAudio">
                    <div class="flex flex-column gap-1">
                      <label for="snippetFadeOut">Fade-out (seconds)</label>
                      <InputNumber
                        id="snippetFadeOut"
                        v-model="newShowFadeOut"
                        :minFractionDigits="1"
                        :maxFractionDigits="1"
                        :disabled="!audioSnippetsEnabled"
                        class="w-full"
                        :class="{ 'p-invalid': fadeOutError }"
                      />
                      <small :class="fadeOutError ? 'p-error' : 'text-500'">
                        {{
                          fadeOutError ||
                          "Baked into new snippets only. 0 = off."
                        }}
                      </small>
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <div class="add-show-column">
              <div class="add-show-column-header">
                <span class="add-show-column-title"
                  >Video Snippets Automation</span
                >
                <InputSwitch v-model="videoSnippetsEnabled" />
              </div>
              <p class="add-show-column-desc">
                Each time this show gets a new video, a video snippet is
                generated automatically.
              </p>

              <div
                class="flex flex-column gap-3"
                :class="{ 'add-show-column-disabled': !videoSnippetsEnabled }"
              >
                <div class="flex flex-column gap-1">
                  <label for="videoSnippetLength"
                    >Snippet Length (seconds)</label
                  >
                  <InputNumber
                    id="videoSnippetLength"
                    v-model="newVideoSnippetLength"
                    :disabled="!videoSnippetsEnabled"
                    class="w-full"
                    :class="{ 'p-invalid': videoDurationError }"
                  />
                  <small :class="videoDurationError ? 'p-error' : 'text-500'">
                    {{ videoDurationError || "Between 60 and 180 seconds." }}
                  </small>
                </div>

                <div class="flex flex-column gap-1">
                  <label for="videoSnippetsPerEpisode"
                    >Snippets per Episode</label
                  >
                  <InputNumber
                    id="videoSnippetsPerEpisode"
                    v-model="newVideoSnippetsPerEpisode"
                    :min="1"
                    :max="5"
                    :disabled="!videoSnippetsEnabled"
                    class="w-full"
                    :class="{ 'p-invalid': videoSnippetsPerEpisodeError }"
                  />
                  <small
                    :class="
                      videoSnippetsPerEpisodeError ? 'p-error' : 'text-500'
                    "
                  >
                    {{
                      videoSnippetsPerEpisodeError ||
                      "1 to 5. Required when automation is enabled."
                    }}
                  </small>
                </div>

                <div class="flex align-items-center justify-content-between">
                  <label for="videoCaptionsDisabled">Disable Captions</label>
                  <InputSwitch
                    id="videoCaptionsDisabled"
                    v-model="newVideoCaptionsDisabled"
                    :disabled="!videoSnippetsEnabled"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="flex flex-column gap-3">
          <p class="m-0">{{ addShowConfirmMessage }}</p>
        </div>
      </div>
      <template #footer>
        <Button
          v-if="!showAddShowConfirmStep"
          label="Cancel"
          outlined
          :disabled="addShowSubmitting"
          @click="showAddShowDialog = false"
        />
        <Button
          v-else
          label="Back"
          outlined
          :disabled="addShowSubmitting"
          @click="cancelAddShowConfirmStep"
        />

        <Button
          v-if="editingShow"
          label="Save"
          :loading="addShowSubmitting"
          :disabled="snippetConfigInvalid"
          @click="saveShowConfig"
        />
        <Button
          v-else-if="!showAddShowConfirmStep"
          label="Add"
          :disabled="!showToAdd || snippetConfigInvalid"
          @click="goToAddShowConfirmStep"
        />
        <Button
          v-else
          label="Yes, Continue"
          :loading="addShowSubmitting"
          @click="addShow"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="showEpisodeSelectDialog"
      header="Generate Snippets"
      modal
      :style="{ width: '34rem' }"
    >
      <template v-if="!episodeSelectType">
        <p v-if="episodeSelectShow" class="episode-select-subtitle">
          What type of snippets do you want to generate for "{{
            episodeSelectShow.show_title
          }}"?
        </p>
        <div class="type-select-row">
          <div class="type-select-option" @click="selectEpisodeType('audio')">
            <i class="pi pi-microphone"></i>
            <span>Audio</span>
          </div>
          <div class="type-select-option" @click="selectEpisodeType('video')">
            <i class="pi pi-video"></i>
            <span>Video</span>
          </div>
        </div>
      </template>

      <template v-else-if="episodeSelectType === 'audio'">
        <p v-if="episodeSelectShow" class="episode-select-subtitle">
          Select episodes from "{{ episodeSelectShow.show_title }}" to generate
          snippets for. Episodes already marked "Has snippets" can still be
          selected to generate more.
        </p>

        <div v-if="episodeSelectLoading" class="episode-select-state">
          <i class="pi pi-spin pi-spinner"></i> Loading episodes…
        </div>
        <div v-else-if="!episodesForSelect.length" class="episode-select-state">
          No episodes found for this show.
        </div>
        <div v-else class="episode-select-list">
          <div
            v-for="episode in episodesForSelect"
            :key="episode.episode_id"
            class="episode-select-row"
            :class="{
              disabled: episodeRemainingCapacity(episode.episode_id) === 0,
            }"
            @click="toggleEpisodeSelection(episode.episode_id)"
          >
            <Checkbox
              :model-value="selectedEpisodeIds.includes(episode.episode_id)"
              binary
              :disabled="episodeRemainingCapacity(episode.episode_id) === 0"
              class="episode-select-checkbox"
            />
            <span class="episode-select-title">{{
              episode.episode_title
            }}</span>
            <span class="episode-select-date">{{
              formatDate(episode.publication_date)
            }}</span>
            <Tag
              v-if="episode.snippet_count > 0"
              :value="`Has ${episode.snippet_count} snippet${
                episode.snippet_count === 1 ? '' : 's'
              }`"
              severity="secondary"
            />
            <GenerationRowSettings
              v-if="selectedEpisodeIds.includes(episode.episode_id)"
              :count="episodeSettings[episode.episode_id]?.count"
              :duration="episodeSettings[episode.episode_id]?.duration"
              :max-count="episodeRemainingCapacity(episode.episode_id)"
              @update:count="
                (value) => setEpisodeCount(episode.episode_id, value)
              "
              @update:duration="
                (value) => setEpisodeDuration(episode.episode_id, value)
              "
            />
          </div>
        </div>
      </template>

      <template v-else-if="episodeSelectType === 'video'">
        <div class="video-source-select">
          <label>Video Source</label>
          <Dropdown
            :modelValue="videoSourceType"
            :options="[
              { label: 'Static', value: 'static' },
              { label: 'Dynamic', value: 'dynamic' },
            ]"
            optionLabel="label"
            optionValue="value"
            class="w-full"
            @update:modelValue="selectVideoSourceType"
          />
        </div>

        <div
          v-if="videoSourceType === 'dynamic'"
          class="flex align-items-center justify-content-between"
        >
          <label for="dynamicVideoCaptionsDisabled">Disable Captions</label>
          <InputSwitch
            id="dynamicVideoCaptionsDisabled"
            v-model="dynamicVideoCaptionsDisabled"
          />
        </div>

        <p
          v-if="episodeSelectShow && videoSourceType === 'static'"
          class="episode-select-subtitle"
        >
          Select snippets from "{{ episodeSelectShow.show_title }}" to generate
          videos for. Snippets that already have a video are disabled. Video
          will be generated based on the episode/show cover image.
        </p>
        <p v-else-if="episodeSelectShow" class="episode-select-subtitle">
          Select imported videos from "{{ episodeSelectShow.show_title }}" to
          generate snippet videos from.
        </p>

        <div v-if="episodeSelectLoading" class="episode-select-state">
          <i class="pi pi-spin pi-spinner"></i> Loading…
        </div>

        <template v-else-if="videoSourceType === 'static'">
          <div v-if="!snippetsForSelect.length" class="episode-select-state">
            No snippets found for this show.
          </div>
          <div v-else class="episode-select-list">
            <div
              v-for="snippet in snippetsForSelect"
              :key="snippet.snippet_id"
              class="video-select-row"
              :class="{ disabled: snippet.has_video }"
            >
              <div
                class="video-select-row-header"
                @click="
                  !snippet.has_video &&
                    toggleSnippetSelection(snippet.snippet_id)
                "
              >
                <Checkbox
                  :model-value="selectedSnippetIds.includes(snippet.snippet_id)"
                  binary
                  :disabled="snippet.has_video"
                  class="episode-select-checkbox"
                />
                <span class="episode-select-title">{{
                  snippet.episode_title
                }}</span>
                <Tag
                  v-if="snippet.has_video"
                  value="Has video"
                  severity="secondary"
                />
              </div>
              <audio
                controls
                preload="none"
                class="snippet-audio-player"
                :src="snippet.snippet_audio_url"
              />
            </div>
          </div>
        </template>

        <template v-else>
          <div
            v-if="!podcastVideosForSelect.length"
            class="episode-select-state"
          >
            This show does not have any videos imported.
          </div>
          <div v-else class="episode-select-list">
            <div
              v-for="video in podcastVideosForSelect"
              :key="video.video_id"
              class="video-select-row"
              :class="{
                disabled: podcastVideoRemainingCapacity(video.video_id) === 0,
              }"
            >
              <div
                class="video-select-row-header"
                @click="togglePodcastVideoSelection(video.video_id)"
              >
                <Checkbox
                  :model-value="
                    selectedPodcastVideoIds.includes(video.video_id)
                  "
                  binary
                  :disabled="
                    podcastVideoRemainingCapacity(video.video_id) === 0
                  "
                  class="episode-select-checkbox"
                />
                <span class="episode-select-title">{{
                  video.title || "Untitled video"
                }}</span>
                <Tag
                  v-if="video.dynamic_snippet_count > 0"
                  :value="`Has ${video.dynamic_snippet_count} video snippet${
                    video.dynamic_snippet_count === 1 ? '' : 's'
                  }`"
                  severity="secondary"
                />
              </div>
              <video
                controls
                controlsList="nofullscreen"
                preload="metadata"
                class="podcast-video-preview"
                :src="video.video_url"
              />
              <GenerationRowSettings
                v-if="selectedPodcastVideoIds.includes(video.video_id)"
                :count="podcastVideoSettings[video.video_id]?.count"
                :duration="podcastVideoSettings[video.video_id]?.duration"
                :max-count="podcastVideoRemainingCapacity(video.video_id)"
                @update:count="
                  (value) => setPodcastVideoCount(video.video_id, value)
                "
                @update:duration="
                  (value) => setPodcastVideoDuration(video.video_id, value)
                "
              />
            </div>
          </div>
        </template>
      </template>

      <template #footer>
        <Button label="Cancel" text @click="closeEpisodeSelectDialog" />
        <Button
          v-if="episodeSelectType === 'audio'"
          label="Generate"
          :disabled="!selectedEpisodeIds.length"
          :loading="generatingSnippets"
          @click="confirmGenerateSnippetsForSelectedEpisodes"
        />
        <Button
          v-if="episodeSelectType === 'video' && videoSourceType === 'static'"
          label="Generate"
          :disabled="!selectedSnippetIds.length"
          :loading="generatingVideos"
          @click="confirmGenerateVideosForSelectedSnippets"
        />
        <Button
          v-if="episodeSelectType === 'video' && videoSourceType === 'dynamic'"
          label="Generate"
          :disabled="!selectedPodcastVideoIds.length"
          :loading="generatingVideos"
          @click="confirmGenerateVideosForSelectedPodcastVideos"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="showViewVideosDialog"
      header="View Video Snippets"
      modal
      :style="{ width: '26rem' }"
    >
      <p class="episode-select-subtitle">
        Which video snippets do you want to view for "{{
          viewVideosShow?.show_title
        }}"?
      </p>
      <div class="type-select-row">
        <div class="type-select-option" @click="selectViewVideosType('static')">
          <i class="pi pi-image"></i>
          <span>Static</span>
        </div>
        <div
          class="type-select-option"
          @click="selectViewVideosType('dynamic')"
        >
          <i class="pi pi-video"></i>
          <span>Dynamic</span>
        </div>
      </div>
      <template #footer>
        <Button label="Cancel" text @click="showViewVideosDialog = false" />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import Card from "primevue/card";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import InputSwitch from "primevue/inputswitch";
import Dropdown from "primevue/dropdown";
import Badge from "primevue/badge";
import Checkbox from "primevue/checkbox";
import Tag from "primevue/tag";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import Calendar from "primevue/calendar";
import { formatDate } from "../../helpers";
import TranscriptDialog from "./TranscriptDialog.vue";
import RegenerationStatusCell from "./shared/RegenerationStatusCell.vue";
import ValidationNoteCell from "./shared/ValidationNoteCell.vue";
import SnippetsConfirmDialog from "./shared/SnippetsConfirmDialog.vue";
import GenerationRowSettings from "./shared/GenerationRowSettings.vue";
import AutomationBadge from "./shared/AutomationBadge.vue";
import AdminPodcastGeneratedVideos from "../PodcastGeneratedVideos/AdminPodcastGeneratedVideos.vue";
import { useSnippetsLibrary } from "./composables/useSnippetsLibrary";
import { useSnippetShows } from "./composables/useSnippetShows";
import { useTabState } from "@/composables/useTabState";
import { useRoute } from "vue-router";

export default {
  components: {
    Card,
    DataTable,
    Column,
    Button,
    Dialog,
    InputText,
    InputNumber,
    InputSwitch,
    Dropdown,
    Badge,
    Checkbox,
    Tag,
    TabView,
    TabPanel,
    Calendar,
    TranscriptDialog,
    RegenerationStatusCell,
    ValidationNoteCell,
    SnippetsConfirmDialog,
    GenerationRowSettings,
    AutomationBadge,
    AdminPodcastGeneratedVideos,
  },
  setup() {
    const route = useRoute();
    const token = localStorage.getItem("adminToken");
    // ?tab=shows in the URL, so a reload lands on the tab you were on.
    const tabs = ["library", "video", "shows"];
    const { tab, setTab } = useTabState(tabs, "library");
    const activeTabIndex = computed(() => tabs.indexOf(tab.value));

    const library = useSnippetsLibrary(token);
    const showsTab = useSnippetShows(token);

    const videoLibraryShowId = ref(null);
    const videoSubTabIndex = ref(0);

    const viewSnippetsForShow = (show) => {
      library.searchQuery.value = show.show_title;
      library.currentPage.value = 1;
      setTab("library");
    };

    const viewVideosForShow = (show, sourceType) => {
      videoLibraryShowId.value = show.show_id;
      videoSubTabIndex.value = sourceType === "dynamic" ? 1 : 0;
      setTab("video");
    };

    const showViewVideosDialog = ref(false);
    const viewVideosShow = ref(null);

    const openViewVideosDialog = (show) => {
      viewVideosShow.value = show;
      showViewVideosDialog.value = true;
    };

    const selectViewVideosType = (sourceType) => {
      viewVideosForShow(viewVideosShow.value, sourceType);
      showViewVideosDialog.value = false;
    };

    const canRemoveShow = (show) =>
      show.active_seller_count === 0 &&
      show.snippet_count === 0 &&
      !show.podcast_generated_videos_count;

    const removeShowTooltip = (show) => {
      if (show.active_seller_count > 0) {
        return "Cannot remove: sellers still active for this show";
      }
      if (show.snippet_count > 0 || show.podcast_generated_videos_count > 0) {
        return "Cannot remove: show already has generated content";
      }
      return "Remove Show";
    };

    const addShowStepWrapper = ref(null);

    watch(showsTab.showAddShowConfirmStep, async () => {
      const el = addShowStepWrapper.value;
      if (!el) return;

      const startHeight = el.offsetHeight;
      await nextTick();

      el.style.height = "auto";
      const endHeight = el.scrollHeight;
      el.style.height = `${startHeight}px`;

      requestAnimationFrame(() => {
        el.style.height = `${endHeight}px`;
      });
      setTimeout(() => {
        el.style.height = "auto";
      }, 260);
    });

    watch(tab, (current) => {
      if (current === "library") {
        library.fetchSnippets();
      } else if (current === "shows") {
        library.resetFilters();
        showsTab.fetchShows();
      }
    });

    onMounted(() => {
      if (route.query.snippetId) {
        library.snippetIdFilter.value = route.query.snippetId;
      }
      if (tab.value === "shows") showsTab.fetchShows();
      else if (tab.value === "library") library.fetchSnippets();
    });

    return {
      tabs,
      tab,
      setTab,
      activeTabIndex,
      viewSnippetsForShow,
      videoLibraryShowId,
      videoSubTabIndex,
      showViewVideosDialog,
      viewVideosShow,
      openViewVideosDialog,
      selectViewVideosType,
      canRemoveShow,
      removeShowTooltip,
      addShowStepWrapper,
      formatDate,
      ...library,
      ...showsTab,
    };
  },
};
</script>

<style scoped>
.round-icon-btn {
  width: 1.9rem;
  height: 1.9rem;
  padding: 0;
  flex-shrink: 0;
}

.show-actions-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
}

.snippet-toggle-btn {
  padding: 0.25rem 0.75rem;
  font-size: 0.8rem;
}

.add-show-step-wrapper {
  overflow: hidden;
  transition: height 0.25s ease;
}

.add-show-columns {
  display: flex;
  gap: 1.5rem;
}

.add-show-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
}

.add-show-column-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--surface-border);
}

.add-show-column-title {
  font-weight: 600;
}

.add-show-intro {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.45;
  color: var(--text-color-secondary);
}

.add-show-column-desc {
  margin: -0.5rem 0 0 0;
  font-size: 0.78rem;
  line-height: 1.4;
  color: var(--text-color-secondary);
}

.add-show-column-disabled {
  opacity: 0.5;
  pointer-events: none;
}

.episode-select-subtitle {
  margin: 0 0 1rem 0;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
}

.episode-select-state {
  padding: 2rem 0;
  text-align: center;
  color: var(--text-color-secondary);
}

.episode-select-list {
  max-height: 22rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.episode-select-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--surface-border);
  border-radius: 6px;
  cursor: pointer;
}

.episode-select-row.disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.episode-select-checkbox {
  pointer-events: none;
}

.video-select-row {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--surface-border);
  border-radius: 6px;
}

.video-select-row.disabled {
  opacity: 0.55;
}

.video-select-row-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
}

.video-select-row.disabled .video-select-row-header {
  cursor: not-allowed;
}

.type-select-row {
  display: flex;
  gap: 0.75rem;
}

.type-select-option {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.5rem 1rem;
  border: 1px solid var(--surface-border);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.type-select-option i {
  font-size: 1.5rem;
}

.type-select-option:hover {
  border-color: var(--primary-color);
  background: rgba(255, 90, 87, 0.08);
}

.video-source-select {
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-bottom: 1rem;
}

.video-source-select label {
  font-size: 11.5px;
  font-weight: 600;
  color: var(--text-color-secondary);
}

.podcast-video-preview {
  height: 10rem;
}

.episode-select-title {
  flex: 1;
  font-size: 0.9rem;
}

.episode-select-date {
  color: var(--text-color-secondary);
  font-size: 0.8rem;
}

:deep(.p-calendar .p-datepicker-trigger) {
  background: transparent;
  border: 1px solid #ced4da;
  border-left: none;
  color: #6c757d;
}

:deep(.p-calendar .p-datepicker-trigger:hover) {
  background: rgba(0, 0, 0, 0.04);
  color: #495057;
}

.snippet-audio-player {
  height: 2.25rem;
  max-width: 16rem;
}

.episode-title-cell {
  display: inline-block;
  max-width: 14rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}
</style>
